/**
 * Unified Time Off Request Form
 * 
 * One intelligent form that adapts based on leave type.
 * Uses time_off_reasons table for eligibility rules and requirements.
 * 
 * Features:
 * - Category → Reason selection with instant eligibility check
 * - Dynamic fields based on leave type
 * - Document upload integration
 * - Manager guidance on statutory rights
 * - Audit trail logging
 * 
 * Updated: Professional SVG icons
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Info,
  CalendarToday,
  Description,
  Upload,
  Send,
  ArrowBack,
  ArrowForward,
  AccessTime,
  AttachMoney,
  Gavel,
  ChildCare,
  LocalHospital,
  Favorite,
  Work
} from '@mui/icons-material';
import NeonatalCareFields from './NeonatalCareFields';
import { DocumentUpload, uploadQueuedDocument } from './DocumentUpload';
import api from './smartApi';

// ============================================================
// PROFESSIONAL SVG ICONS
// Consistent 24x24, stroke-based design
// ============================================================

const CategoryIcons = {
  // Annual Leave - Sun icon
  AnnualLeave: ({ color = 'currentColor', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"></circle>
      <line x1="12" y1="1" x2="12" y2="3"></line>
      <line x1="12" y1="21" x2="12" y2="23"></line>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
      <line x1="1" y1="12" x2="3" y2="12"></line>
      <line x1="21" y1="12" x2="23" y2="12"></line>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
    </svg>
  ),

  // Health & Recovery - Heart with pulse
  HealthRecovery: ({ color = 'currentColor', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      <polyline points="3 12 6 12 8 10 10 14 12 12 15 12"></polyline>
    </svg>
  ),

  // Family & Parenting - Users/family icon
  FamilyParenting: ({ color = 'currentColor', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="3"></circle>
      <path d="M12 8v4"></path>
      <circle cx="6" cy="12" r="2"></circle>
      <circle cx="18" cy="12" r="2"></circle>
      <path d="M6 14v3a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-3"></path>
      <path d="M12 12v7"></path>
    </svg>
  ),

  // Care & Compassion - Hands holding heart
  CareCompassion: ({ color = 'currentColor', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L9 5.5C7.5 7 7.5 9.5 9 11L12 14L15 11C16.5 9.5 16.5 7 15 5.5L12 2Z"></path>
      <path d="M4.5 13.5L2 16v4h4l2-2"></path>
      <path d="M19.5 13.5L22 16v4h-4l-2-2"></path>
      <path d="M12 14v4"></path>
      <path d="M8 18h8"></path>
    </svg>
  ),

  // Civic & Contractual - Scales of justice
  CivicContractual: ({ color = 'currentColor', size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21"></line>
      <path d="M5 8L12 5l7 3"></path>
      <path d="M5 8v0a3 3 0 0 0 3 3 3 3 0 0 0 3-3"></path>
      <path d="M13 8v0a3 3 0 0 0 3 3 3 3 0 0 0 3-3"></path>
      <line x1="8" y1="21" x2="16" y2="21"></line>
    </svg>
  ),
};

// Icon wrapper component for consistent sizing and styling
const CategoryIcon = ({ category, size = 32 }) => {
  const iconConfig = CATEGORIES[category];
  if (!iconConfig) return null;
  
  const IconComponent = iconConfig.iconComponent;
  return (
    <Box
      sx={{
        width: size + 16,
        height: size + 16,
        borderRadius: '12px',
        backgroundColor: `${iconConfig.color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconComponent color={iconConfig.color} size={size} />
    </Box>
  );
};

// ============================================================
// CATEGORY & REASON CONFIGURATION
// This mirrors the time_off_reasons database table
// ============================================================

const CATEGORIES = {
  annual_leave: {
    key: 'annual_leave',
    name: 'Annual Leave',
    iconComponent: CategoryIcons.AnnualLeave,
    color: '#0D9488',
    description: 'Paid holiday from your entitlement'
  },
  health_recovery: {
    key: 'health_recovery',
    name: 'Health & Recovery',
    iconComponent: CategoryIcons.HealthRecovery,
    color: '#DC2626',
    description: 'Sickness, medical appointments, recovery'
  },
  family_parenting: {
    key: 'family_parenting',
    name: 'Family & Parenting',
    iconComponent: CategoryIcons.FamilyParenting,
    color: '#7C3AED',
    description: 'Maternity, paternity, parental, adoption'
  },
  care_compassion: {
    key: 'care_compassion',
    name: 'Care & Compassion',
    iconComponent: CategoryIcons.CareCompassion,
    color: '#0284C7',
    description: "Carer's leave, bereavement, emergencies"
  },
  civic_contractual: {
    key: 'civic_contractual',
    name: 'Civic & Contractual',
    iconComponent: CategoryIcons.CivicContractual,
    color: '#64748B',
    description: 'Jury service, public duties'
  }
};

// Leave type configurations with field requirements
const LEAVE_TYPES = {
  // Annual Leave
  standard_holiday: {
    key: 'standard_holiday',
    name: 'Standard Holiday',
    category: 'annual_leave',
    fields: ['start_date', 'end_date'],
    documents: [],
    eligibility: { day_one_right: true },
    entitlement: '5.6 weeks pro-rata (28 days FTE)',
    pay: 'Full pay',
    canRefuse: true,
    guidance: 'Check team coverage before approving. Can refuse with valid business reason.'
  },

  // Health & Recovery
  sickness: {
    key: 'sickness',
    name: 'Sickness / Medical',
    category: 'health_recovery',
    fields: ['start_date', 'end_date_unknown', 'reason_brief'],
    documents: ['fit_note'],
    documentCondition: 'if_over_7_days',
    eligibility: { 
      day_one_right: true,  // FROM APRIL 2026
      day_one_right_effective: '2026-04-06',
      current_rules: {
        requirement: 'Earnings above £123/week (LEL)',
        waiting_days: 4
      },
      april_2026_rules: {
        day_one_right: true,
        waiting_days: 0
      },
      change_note: 'SSP becomes day-one right from April 2026 with waiting days abolished'
    },
    entitlement: 'Duration of illness',
    pay: 'SSP From April 2026: from day 1 right',
    canRefuse: false,
    guidance: 'Cannot refuse genuine sickness. Request fit note if >7 days. Monitor patterns with Bradford Factor. FROM APRIL 2026: SSP is day-one right with no waiting days.'
  },

  // Family & Parenting
  maternity: {
    key: 'maternity',
    name: 'Maternity Leave',
    category: 'family_parenting',
    fields: ['due_date', 'intended_start_date', 'leave_duration'],
    documents: ['matb1'],
    eligibility: { 
      day_one_right: true,
      smp_requirement: '26 weeks service by 15th week before EWC'
    },
    entitlement: '52 weeks (39 paid if eligible for SMP)',
    pay: 'SMP: 90% of average weekly earnings for 6 weeks, then the lower of the current statutory weekly rate and 90% for 33 weeks',
    canRefuse: false,
    notice: '15 weeks before EWC',
    guidance: 'Statutory right - cannot refuse. Check SMP eligibility. Start date can be changed with 28 days notice.'
  },

  paternity: {
    key: 'paternity',
    name: 'Paternity Leave',
    category: 'family_parenting',
    fields: ['due_date', 'intended_start_date', 'leave_type_paternity', 'relationship'],
    documents: ['matb1', 'birth_certificate'],
    eligibility: { 
      day_one_right: true,
      spp_requirement: '26 weeks service by 15th week before EWC'
    },
    entitlement: '2 weeks (can be taken as 2 separate weeks from April 2024)',
    pay: 'SPP: the current statutory weekly rate, or 90% of average weekly earnings if lower (if eligible)',
    canRefuse: false,
    notice: '15 weeks before EWC',
    guidance: 'Statutory right - cannot refuse. Can now be taken as 2 separate weeks within 52 weeks of birth.'
  },

  adoption: {
    key: 'adoption',
    name: 'Adoption Leave',
    category: 'family_parenting',
    fields: ['match_date', 'placement_date', 'intended_start_date'],
    documents: ['matching_certificate'],
    eligibility: { 
      day_one_right: true,
      sap_requirement: '26 weeks service by match week'
    },
    entitlement: '52 weeks (39 paid if eligible for SAP)',
    pay: 'SAP: Same as SMP',
    canRefuse: false,
    guidance: 'Same rights as maternity. One parent takes adoption leave, other can take paternity.'
  },

  shared_parental: {
    key: 'shared_parental',
    name: 'Shared Parental Leave',
    category: 'family_parenting',
    fields: ['original_leave_type', 'weeks_requested', 'pattern_requested'],
    documents: ['curtailment_notice', 'partner_declaration'],
    eligibility: { 
      min_service_weeks: 26,
      continuous_employment: true
    },
    entitlement: 'Up to 50 weeks shared between parents',
    pay: 'ShPP: the current statutory weekly rate, or 90% of average weekly earnings if lower, for remaining eligible weeks',
    canRefuse: false,
    notice: '8 weeks before each block',
    guidance: 'Complex - ensure both parents meet eligibility. Can request up to 3 blocks.'
  },

  neonatal_care: {
    key: 'neonatal_care',
    name: 'Neonatal Care Leave',
    category: 'family_parenting',
    fields: ['custom'],  // Use custom component instead of standard fields
    customComponent: 'NeonatalCareFields',
    documents: ['hospital_confirmation'],
    eligibility: { day_one_right: true },
    entitlement: 'Up to 12 weeks',
    pay: 'Statutory Neonatal Care Pay (if eligible)',
    canRefuse: false,
    isNew: true,
    newDate: 'April 2025',
    guidance: 'Day-one right. Tier 1 while baby in care (flexible). Tier 2 after discharge (consecutive).'
  },
  
  unpaid_parental: {
    key: 'unpaid_parental',
    name: 'Unpaid Parental Leave',
    category: 'family_parenting',
    fields: ['child_name', 'child_dob', 'weeks_requested', 'pattern_requested'],
    documents: [],
    eligibility: { min_service_weeks: 52 },
    entitlement: '18 weeks per child until age 18',
    pay: 'Unpaid',
    canRefuse: true,
    refuseCondition: 'Can postpone (not refuse) for up to 6 months for business reasons',
    notice: '21 days',
    guidance: 'Can postpone for business reasons but not refuse entirely. Max 4 weeks per year unless agreed.'
  },

  // Care & Compassion
  carers_leave: {
    key: 'carers_leave',
    name: "Carer's Leave",
    category: 'care_compassion',
    fields: ['care_recipient', 'relationship', 'start_date', 'days_requested'],
    documents: [],
    eligibility: { day_one_right: true },
    entitlement: '1 week (5 days) per year',
    pay: 'Unpaid',
    canRefuse: true,
    refuseCondition: 'Can postpone if would unduly disrupt business',
    notice: 'Twice leave length, minimum 3 days',
    guidance: 'Day-one right. Can take in half days. For long-term care needs, not emergencies.'
  },

  emergency_dependant: {
    key: 'emergency_dependant',
    name: 'Emergency Dependant Leave',
    category: 'care_compassion',
    fields: ['emergency_details', 'dependant_relationship'],
    documents: [],
    eligibility: { day_one_right: true },
    entitlement: 'Reasonable time (usually 1-2 days)',
    pay: 'Unpaid (though many employers pay)',
    canRefuse: false,
    guidance: 'Cannot refuse genuine emergencies. For unexpected situations only, not planned care.'
  },

  parental_bereavement: {
    key: 'parental_bereavement',
    name: 'Parental Bereavement Leave',
    category: 'care_compassion',
    fields: ['date_of_death', 'relationship'],
    documents: [],
    eligibility: { day_one_right: true },
    entitlement: '2 weeks',
    pay: 'Statutory Parental Bereavement Pay (if eligible)',
    canRefuse: false,
    guidance: "Jack's Law. Cannot refuse. Can be taken as 2 separate weeks within 56 weeks."
  },

  pregnancy_loss: {
    key: 'pregnancy_loss',
    name: 'Pregnancy Loss Leave',
    category: 'care_compassion',
    fields: ['date_of_loss'],
    documents: [],
    eligibility: { day_one_right: true },
    entitlement: 'TBC by legislation',
    pay: 'TBC',
    canRefuse: false,
    isNew: true,
    newDate: '2026',
    guidance: 'PROPOSED - For losses before 24 weeks. Legislation pending.'
  },

  // Civic & Contractual
  jury_service: {
    key: 'jury_service',
    name: 'Jury Service',
    category: 'civic_contractual',
    fields: ['court_date', 'court_name', 'expected_duration'],
    documents: ['jury_summons'],
    eligibility: { day_one_right: true },
    entitlement: 'Duration of service',
    pay: 'Unpaid (claim from court)',
    canRefuse: false,
    guidance: 'Cannot refuse. Employee claims loss of earnings from court. Can request deferral from court if critical business need.'
  }
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const TimeOffRequestForm = ({ employeeId, employeeData, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  // Where to go when finished: back to the employee portal if signed in there, else the manager hub
  const goBack = () => navigate(window.location.pathname.toLowerCase().startsWith('/employeeportal') ? '/employeeportal' : '/time-off-hub');
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [formData, setFormData] = useState({});
  const [pendingDoc, setPendingDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Get current leave type config
  const leaveConfig = selectedReason ? LEAVE_TYPES[selectedReason] : null;

  // Check eligibility when reason changes
  useEffect(() => {
    if (selectedReason && employeeData) {
      checkEligibility();
    }
  }, [selectedReason, employeeData]);

  const checkEligibility = async () => {
    try {
      const response = await api.get(`/time-off/eligibility/${selectedReason}`, {
        params: { employee_id: employeeId }
      });
      setEligibility(response.data);
    } catch (err) {
      // Fallback to client-side check
      const config = LEAVE_TYPES[selectedReason];
      if (config?.eligibility?.day_one_right) {
        setEligibility({
          eligible: true,
          message: 'You are eligible (day-one right)',
          service_weeks: employeeData?.service_weeks || 0
        });
      } else {
        setEligibility({
          eligible: true,
          message: 'Eligibility check pending',
          service_weeks: employeeData?.service_weeks || 0
        });
      }
    }
  };

  // Get reasons for selected category
  const getReasonsForCategory = () => {
    return Object.values(LEAVE_TYPES).filter(lt => lt.category === selectedCategory);
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    // Maternity, paternity and adoption ask for an "intended start date". The rest of the form and the server
    // call it start_date, so keep the two in step. Without this, Continue stayed disabled and the request
    // could never be submitted.
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'intended_start_date' ? { start_date: value } : {})
    }));
  };

  // Calculate working days between dates
  const calculateDays = (start, end) => {
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    let days = 0;
    let current = new Date(startDate);
    
    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) days++;
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  // Check if fit note is required (sickness > 7 days)
  const isFitNoteRequired = () => {
    if (selectedReason !== 'sickness') return false;
    if (!formData.start_date) return false;
    
    const endDate = formData.end_date || new Date();
    const days = calculateDays(formData.start_date, endDate);
    return days > 7;
  };

  // Submit request
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        employee_id: employeeId,
        category: selectedCategory,
        reason_type: selectedReason,
        ...formData,
        total_days: calculateDays(formData.start_date, formData.end_date)
      };

      const response = await api.post('/time-off/requests', payload);

      // Send the document chosen on the form, now that the request has an ID. A failure here must not hide
      // the fact that the request itself went through.
      let docProblem = false;
      if (pendingDoc && response.data && response.data.request_id) {
        try {
          await uploadQueuedDocument(pendingDoc, 'time_off_requests', response.data.request_id);
        } catch (uploadErr) {
          docProblem = true;
        }
      }
      
      setSnackbar({
        open: true,
        message: docProblem
          ? 'Request submitted, but the document could not be uploaded. Please add it from your requests list.'
          : 'Request submitted successfully',
        severity: docProblem ? 'warning' : 'success'
      });

      if (onSuccess) {
        onSuccess(response.data);
      } else {
        setTimeout(goBack, docProblem ? 4000 : 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Stepper steps
  const steps = ['Select Type', 'Enter Details', 'Review & Submit'];

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  // Step 1: Category & Reason Selection
  const renderStepOne = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        What type of time off do you need?
      </Typography>

      {/* Category Selection */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.values(CATEGORIES).map(cat => {
          const IconComponent = cat.iconComponent;
          return (
            <Grid item xs={12} sm={6} md={4} key={cat.key}>
              <Card
                variant={selectedCategory === cat.key ? 'elevation' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  borderColor: selectedCategory === cat.key ? cat.color : 'divider',
                  borderWidth: selectedCategory === cat.key ? 2 : 1,
                  backgroundColor: selectedCategory === cat.key ? `${cat.color}10` : 'background.paper',
                  transition: 'all 0.2s ease',
                  '&:hover': { 
                    borderColor: cat.color,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
                onClick={() => {
                  setSelectedCategory(cat.key);
                  setSelectedReason('');
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: selectedCategory === cat.key ? cat.color : `${cat.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <IconComponent 
                        color={selectedCategory === cat.key ? '#ffffff' : cat.color} 
                        size={24} 
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {cat.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Reason Selection */}
      {selectedCategory && (
        <Collapse in={!!selectedCategory}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Select specific reason:
          </Typography>
          
          <Grid container spacing={2}>
            {getReasonsForCategory().map(reason => (
              <Grid item xs={12} sm={6} key={reason.key}>
                <Card
                  variant={selectedReason === reason.key ? 'elevation' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    borderColor: selectedReason === reason.key ? 'primary.main' : 'divider',
                    borderWidth: selectedReason === reason.key ? 2 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { 
                      borderColor: 'primary.light',
                      transform: 'translateY(-2px)',
                    }
                  }}
                  onClick={() => setSelectedReason(reason.key)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Typography variant="subtitle1" fontWeight="medium">
                        {reason.name}
                      </Typography>
                      {reason.isNew && (
                        <Chip 
                          label={`NEW ${reason.newDate}`} 
                          color="secondary" 
                          size="small" 
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {reason.entitlement}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {reason.pay}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      )}

      {/* Eligibility Display */}
      {selectedReason && leaveConfig && (
        <Collapse in={!!selectedReason}>
          <Box sx={{ mt: 3 }}>
            <EligibilityPanel 
              leaveConfig={leaveConfig} 
              eligibility={eligibility}
              employeeData={employeeData}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );

  // Step 2: Enter Details
  const renderStepTwo = () => {
    if (!leaveConfig) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {leaveConfig.name} Details
        </Typography>
        {/* Custom component for neonatal care */}
      {leaveConfig.customComponent === 'NeonatalCareFields' && (
        <NeonatalCareFields
          formData={formData}
          onChange={handleFieldChange}
          employeeId={employeeId}
          employeeServiceWeeks={employeeData?.service_weeks}
        />
      )}
        {/* Dynamic Fields Based on Leave Type */}
        <Grid container spacing={3}>
          {/* Standard Date Fields */}
          {leaveConfig.fields.includes('start_date') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={formData.start_date || ''}
                onChange={(e) => handleFieldChange('start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          )}

          {/* End Date (Standard Holiday) */}
          {leaveConfig.fields.includes('end_date') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Last Day of Holiday"
                value={formData.end_date || ''}
                onChange={(e) => handleFieldChange('end_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.start_date || undefined }}
                helperText="The last day you will be away, not the day you return"
                required
              />
            </Grid>
          )}

          {/* Unknown End Date (Sickness) */}
          {leaveConfig.fields.includes('end_date_unknown') && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Do you know your last day off sick?</InputLabel>
                <Select
                  value={formData.return_known || ''}
                  onChange={(e) => handleFieldChange('return_known', e.target.value)}
                  label="Do you know your last day off sick?"
                >
                  <MenuItem value="unknown">Not yet known</MenuItem>
                  <MenuItem value="specific">Yes, specific date</MenuItem>
                </Select>
              </FormControl>
              {formData.return_known === 'specific' && (
                <TextField
                  fullWidth
                  type="date"
                  label="Last Day Off Sick"
                  value={formData.end_date || ''}
                  onChange={(e) => handleFieldChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="The last day you'll be off — not your return-to-work date"
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
          )}
          

          {/* Due Date (Maternity/Paternity) */}
          {leaveConfig.fields.includes('due_date') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Baby's Due Date (EWC)"
                value={formData.due_date || ''}
                onChange={(e) => handleFieldChange('due_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                helperText="Expected Week of Childbirth"
              />
            </Grid>
          )}

          {/* Intended Start Date */}
          {leaveConfig.fields.includes('intended_start_date') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Intended Start Date"
                value={formData.intended_start_date || ''}
                onChange={(e) => handleFieldChange('intended_start_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                helperText={leaveConfig.key === 'maternity' ? 'Can start up to 11 weeks before due date' : ''}
              />
            </Grid>
          )}

          {/* Leave Duration */}
          {leaveConfig.fields.includes('leave_duration') && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Leave Duration</InputLabel>
                <Select
                  value={formData.leave_duration || ''}
                  onChange={(e) => handleFieldChange('leave_duration', e.target.value)}
                  label="Leave Duration"
                >
                  <MenuItem value="52">Full 52 weeks</MenuItem>
                  <MenuItem value="39">39 weeks (paid period only)</MenuItem>
                  <MenuItem value="26">26 weeks</MenuItem>
                  <MenuItem value="other">Other (specify later)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Paternity Leave Type */}
          {leaveConfig.fields.includes('leave_type_paternity') && (
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  How would you like to take your paternity leave?
                </Typography>
                <RadioGroup
                  value={formData.paternity_pattern || ''}
                  onChange={(e) => handleFieldChange('paternity_pattern', e.target.value)}
                >
                  <FormControlLabel 
                    value="consecutive" 
                    control={<Radio />} 
                    label="2 consecutive weeks" 
                  />
                  <FormControlLabel 
                    value="split" 
                    control={<Radio />} 
                    label="2 separate weeks (within 52 weeks of birth)" 
                  />
                  <FormControlLabel 
                    value="one_week" 
                    control={<Radio />} 
                    label="1 week only" 
                  />
                </RadioGroup>
                <FormHelperText>
                  From April 2024, you can take paternity leave as 2 separate weeks
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          {/* Relationship Field */}
          {leaveConfig.fields.includes('relationship') && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Your Relationship</InputLabel>
                <Select
                  value={formData.relationship || ''}
                  onChange={(e) => handleFieldChange('relationship', e.target.value)}
                  label="Your Relationship"
                >
                  <MenuItem value="father">Father</MenuItem>
                  <MenuItem value="spouse">Spouse/Civil Partner</MenuItem>
                  <MenuItem value="partner">Partner</MenuItem>
                  <MenuItem value="parent">Intended Parent (surrogacy)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Carer's Leave Fields */}
          {leaveConfig.fields.includes('care_recipient') && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Who are you caring for?"
                  value={formData.care_recipient || ''}
                  onChange={(e) => handleFieldChange('care_recipient', e.target.value)}
                  placeholder="e.g., Mother, Father, Spouse"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Days Requested</InputLabel>
                  <Select
                    value={formData.days_requested || ''}
                    onChange={(e) => handleFieldChange('days_requested', e.target.value)}
                    label="Days Requested"
                  >
                    <MenuItem value="0.5">Half day</MenuItem>
                    <MenuItem value="1">1 day</MenuItem>
                    <MenuItem value="2">2 days</MenuItem>
                    <MenuItem value="3">3 days</MenuItem>
                    <MenuItem value="4">4 days</MenuItem>
                    <MenuItem value="5">5 days (full week entitlement)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Jury Service Fields */}
          {leaveConfig.fields.includes('court_name') && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Court Name"
                  value={formData.court_name || ''}
                  onChange={(e) => handleFieldChange('court_name', e.target.value)}
                  placeholder="e.g., Manchester Crown Court"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Expected Duration</InputLabel>
                  <Select
                    value={formData.expected_duration || ''}
                    onChange={(e) => handleFieldChange('expected_duration', e.target.value)}
                    label="Expected Duration"
                  >
                    <MenuItem value="2_weeks">Up to 2 weeks</MenuItem>
                    <MenuItem value="unknown">Unknown (standard jury)</MenuItem>
                    <MenuItem value="extended">Extended trial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* Brief Reason (Sickness) */}
          {leaveConfig.fields.includes('reason_brief') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Brief Reason (Optional)"
                value={formData.reason_brief || ''}
                onChange={(e) => handleFieldChange('reason_brief', e.target.value)}
                placeholder="You don't have to share details, but it may help us support you"
                multiline
                rows={2}
                helperText="This information is confidential"
              />
            </Grid>
          )}

          {/* Emergency Details */}
          {leaveConfig.fields.includes('emergency_details') && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="What happened?"
                value={formData.emergency_details || ''}
                onChange={(e) => handleFieldChange('emergency_details', e.target.value)}
                placeholder="Brief description of the emergency"
                multiline
                rows={2}
                required
              />
            </Grid>
          )}
        </Grid>

        {/* Document Upload Section */}
        {leaveConfig.documents.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              📎 Required Documents
            </Typography>
            
            {leaveConfig.documentCondition === 'if_over_7_days' ? (
              isFitNoteRequired() ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Your absence is over 7 days. You must provide a fit note from your GP.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  A fit note is only required if your absence exceeds 7 calendar days.
                </Alert>
              )
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Document needed: {leaveConfig.documents.map(d => 
                  d === 'matb1' ? 'MATB1 Certificate' :
                  d === 'fit_note' ? 'Fit Note' :
                  d === 'jury_summons' ? 'Jury Summons' :
                  d
                ).join(', ')}
              </Alert>
            )}

            {/* The request does not exist yet, so the chosen file is held here and sent as soon as it is submitted. */}
            {(leaveConfig.documentCondition !== 'if_over_7_days' || isFitNoteRequired()) && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Choose the document now and it is sent with your request. If you do not have it yet, you can
                  submit without it and add it later.
                </Alert>
                <DocumentUpload
                  sourceTable="time_off_requests"
                  sourceId={null}
                  documentType={leaveConfig.documents[0]}
                  onFileQueued={setPendingDoc}
                />
              </>
            )}
          </Box>
        )}

        {/* Days Calculation */}
        {formData.start_date && formData.end_date && (
          <Box sx={{ mt: 3 }}>
            <Alert severity="info">
              <strong>Working days requested:</strong> {calculateDays(formData.start_date, formData.end_date)} days
            </Alert>
          </Box>
        )}
      </Box>
    );
  };

  // Step 3: Review & Submit
  const renderStepThree = () => {
    if (!leaveConfig) return null;
    const categoryConfig = CATEGORIES[selectedCategory];
    const IconComponent = categoryConfig?.iconComponent;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Review Your Request
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  {IconComponent && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: `${categoryConfig.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <IconComponent color={categoryConfig.color} size={18} />
                    </Box>
                  )}
                  <Typography variant="body1" fontWeight="medium">
                    {leaveConfig.name}
                  </Typography>
                </Box>
              </Grid>
              
              {formData.start_date && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Dates</Typography>
                  <Typography variant="body1">
                    {new Date(formData.start_date).toLocaleDateString('en-GB')}
                    {formData.end_date && ` - ${new Date(formData.end_date).toLocaleDateString('en-GB')}`}
                  </Typography>
                </Grid>
              )}

              {formData.due_date && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Due Date</Typography>
                  <Typography variant="body1">
                    {new Date(formData.due_date).toLocaleDateString('en-GB')}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Entitlement</Typography>
                <Typography variant="body1">{leaveConfig.entitlement}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Pay</Typography>
                <Typography variant="body1">{leaveConfig.pay}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Documents Uploaded */}
        {pendingDoc && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ 1 document attached ({pendingDoc.file.name})
          </Alert>
        )}

        {/* Notice Period Warning */}
        {leaveConfig.notice && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Notice Requirement</AlertTitle>
            {leaveConfig.notice}
          </Alert>
        )}

        {/* Declaration */}
        <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
          <CardContent>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.declaration || false}
                  onChange={(e) => handleFieldChange('declaration', e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I confirm the information provided is accurate and I understand my 
                  entitlements and obligations regarding this leave request.
                </Typography>
              }
            />
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <Paper sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Request Time Off
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && renderStepOne()}
        {activeStep === 1 && renderStepTwo()}
        {activeStep === 2 && renderStepThree()}
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={() => activeStep === 0 ? (onCancel ? onCancel() : goBack()) : setActiveStep(prev => prev - 1)}
          startIcon={<ArrowBack />}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(prev => prev + 1)}
            endIcon={<ArrowForward />}
            disabled={
              (activeStep === 0 && !selectedReason) ||
              (activeStep === 1 && (
                !formData.start_date ||
                (leaveConfig && leaveConfig.fields.includes('end_date') &&
                  (!formData.end_date || formData.end_date < formData.start_date))
              ))
            }
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting || !formData.declaration}
            startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
};


// ============================================================
// ELIGIBILITY PANEL COMPONENT
// ============================================================

const EligibilityPanel = ({ leaveConfig, eligibility, employeeData }) => {
  const isSSP = leaveConfig.key === 'sickness';
  const isAfterApril2026 = new Date() >= new Date('2026-04-06');

  return (
    <Card variant="outlined" sx={{ backgroundColor: eligibility?.eligible ? 'success.lighter' : 'warning.lighter' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {eligibility?.eligible ? (
            <CheckCircle color="success" />
          ) : (
            <Warning color="warning" />
          )}
          <Typography variant="subtitle1" fontWeight="bold">
            {eligibility?.eligible ? 'You are eligible' : 'Eligibility Check'}
          </Typography>
        </Box>

        <List dense>
          {/* SSP Special Handling */}
          {isSSP && (
            <>
              {isAfterApril2026 ? (
                <>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Day-one right" 
                      secondary="SSP available from your first day of employment"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="No waiting days" 
                      secondary="SSP payable from day 1 of sickness"
                    />
                  </ListItem>
                </>
              ) : (
                <>
                  <ListItem>
                    <ListItemIcon><Info color="info" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Current SSP rules (until April 2026)"
                      secondary="Must earn above £123/week • 4 waiting days before SSP starts"
                    />
                  </ListItem>
                  <ListItem sx={{ backgroundColor: 'info.lighter', borderRadius: 1, my: 1 }}>
                    <ListItemIcon><Info color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="🔄 CHANGING APRIL 2026"
                      secondary="SSP becomes day-one right with NO waiting days"
                      primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.main' }}
                    />
                  </ListItem>
                </>
              )}
            </>
          )}

          {/* Day One Right (non-SSP) */}
          {!isSSP && leaveConfig.eligibility?.day_one_right && (
            <ListItem>
              <ListItemIcon><CheckCircle color="success" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Day-one right" 
                secondary="You can request this from your first day of employment"
              />
            </ListItem>
          )}

          {/* Service Requirement */}
          {leaveConfig.eligibility?.min_service_weeks && (
            <ListItem>
              <ListItemIcon>
                {(employeeData?.service_weeks || 0) >= leaveConfig.eligibility.min_service_weeks ? (
                  <CheckCircle color="success" fontSize="small" />
                ) : (
                  <Cancel color="error" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={`Requires ${leaveConfig.eligibility.min_service_weeks} weeks service`}
                secondary={`You have: ${employeeData?.service_weeks || 'Unknown'} weeks`}
              />
            </ListItem>
          )}

          {/* SMP/SPP/SAP Eligibility */}
          {(leaveConfig.eligibility?.smp_requirement || 
            leaveConfig.eligibility?.spp_requirement ||
            leaveConfig.eligibility?.sap_requirement) && (
            <ListItem>
              <ListItemIcon><AttachMoney color="primary" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Statutory Pay Eligibility"
                secondary={
                  leaveConfig.eligibility.smp_requirement ||
                  leaveConfig.eligibility.spp_requirement ||
                  leaveConfig.eligibility.sap_requirement
                }
              />
            </ListItem>
          )}

          {/* Can Refuse Info */}
          <ListItem>
            <ListItemIcon>
              {leaveConfig.canRefuse ? (
                <Warning color="warning" fontSize="small" />
              ) : (
                <Gavel color="primary" fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={leaveConfig.canRefuse ? 'Employer may refuse' : 'Statutory right - cannot be refused'}
              secondary={leaveConfig.refuseCondition || (leaveConfig.canRefuse ? 'Valid business reason required' : 'Your employer must grant this leave')}
            />
          </ListItem>

          {/* Notice Requirement */}
          {leaveConfig.notice && (
            <ListItem>
              <ListItemIcon><AccessTime color="action" fontSize="small" /></ListItemIcon>
              <ListItemText 
                primary="Notice Required"
                secondary={leaveConfig.notice}
              />
            </ListItem>
          )}
        </List>
      </CardContent>
    </Card>
  );
};


export default TimeOffRequestForm;